import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button } from '@picsio/ui';
import picsioConfig from '../../../../../config';
import localization from '../../shared/strings';
import Icon from '../Icon';
import { showDialog } from '../dialog';
import { setDownloadPresets } from '../../store/reducers/user';
import Presets from '../DialogPresets';
import Settings from '../DialogPresets/Settings';
import { defaultPresets, customPreset, defaultPresetConfig } from '../DialogPresets/config';
import * as downloadPresetActions from '../DownloadDialog/actions';

const { isMainApp } = picsioConfig;

const getPresets = (downloadPresets = [], shouldFormatBeDisabled = false) => {
  const presets = isMainApp && !shouldFormatBeDisabled
    ? [...defaultPresets, customPreset, ...downloadPresets]
    : [...defaultPresets];

  return presets.map((preset) => ({
    ...preset,
    data: {
      ...defaultPresetConfig,
      ...preset.data,
    },
  }));
};

function AssetsCopyDialog(props) {
  const {
    destroy,
    shouldFormatBeDisabled,
    onConfirm,
    onCancel,
    downloadPresets,
    setDownloadPresets: updateDownloadPresets,
    sourceMimeType,
  } = props;

  const presets = useMemo(
    () => getPresets(downloadPresets, shouldFormatBeDisabled),
    [downloadPresets, shouldFormatBeDisabled],
  );
  const defaultPreset = useMemo(() => presets[0], [presets]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState(defaultPreset?._id || customPreset._id);
  const [selectedSettings, setSelectedSettings] = useState(defaultPreset?.data || customPreset.data);

  const selectedPreset = useMemo(
    () => presets.find(({ _id }) => _id === selectedPresetId) || defaultPreset || customPreset,
    [defaultPreset, presets, selectedPresetId],
  );
  const selectedPresetView = useMemo(
    () => ({
      ...selectedPreset,
      data: selectedSettings,
    }),
    [selectedPreset, selectedSettings],
  );

  useEffect(() => {
    if (!presets.some(({ _id }) => _id === selectedPresetId)) {
      const fallbackPreset = defaultPreset || customPreset;
      setSelectedPresetId(fallbackPreset._id);
      setSelectedSettings({
        ...fallbackPreset.data,
      });
    }
  }, [defaultPreset, presets, selectedPresetId]);

  const setPresetValue = useCallback((name, value) => {
    setSelectedSettings((prevSettings) => ({
      ...prevSettings,
      [name]: value,
    }));
  }, []);

  const handleChangePreset = useCallback((e) => {
    const {
      target: { value },
    } = e;

    const preset = presets.find(({ _id }) => _id === value);
    if (preset) {
      setSelectedPresetId(preset._id);
      setSelectedSettings({
        ...preset.data,
      });
    }
  }, [presets]);

  const handleChangeSettings = useCallback((e) => {
    const {
      target: {
        type, name, value, checked,
      },
    } = e;
    let validValue = value;

    switch (type) {
    case 'number':
      validValue = value === '' ? '' : Number(value);
      break;
    case 'checkbox':
      validValue = checked;
      break;
    default:
      break;
    }
    setPresetValue(name, validValue);
  }, [setPresetValue]);

  const handleBlurSettings = useCallback((e) => {
    const {
      target: {
        type, name, value,
      },
    } = e;

    if (type === 'number' && !Number(value)) {
      setPresetValue(name, 1);
    }
  }, [setPresetValue]);

  const removePreset = useCallback((presetId) => {
    const nextDownloadPresets = downloadPresets.filter(({ _id }) => _id !== presetId);
    updateDownloadPresets(nextDownloadPresets);
    if (selectedPresetId === presetId) {
      const nextPresets = getPresets(nextDownloadPresets, shouldFormatBeDisabled);
      const fallbackPreset = nextPresets[0] || customPreset;
      setSelectedPresetId(fallbackPreset._id);
      setSelectedSettings({
        ...fallbackPreset.data,
      });
    }
  }, [downloadPresets, selectedPresetId, shouldFormatBeDisabled, updateDownloadPresets]);

  const saveDownloadPreset = useCallback(async (name, shared, isNew) => {
    const { _id } = selectedPreset;

    try {
      setIsLoading(true);
      const requestData = {
        name,
        data: selectedSettings,
        shared,
      };
      let preset;

      if (isNew) {
        preset = await downloadPresetActions.createDownloadPreset(requestData);
      } else {
        preset = await downloadPresetActions.updateDownloadPreset({ _id, ...requestData });
      }

      const nextDownloadPresets = downloadPresets.some(
        ({ _id: itemId }) => itemId === preset._id,
      )
        ? downloadPresets.map((item) => (item._id === preset._id ? preset : item))
        : [...downloadPresets, preset];

      updateDownloadPresets(nextDownloadPresets);
      setSelectedPresetId(preset._id);
      setSelectedSettings({
        ...defaultPresetConfig,
        ...preset.data,
      });
    } catch (e) {
      showDialog({
        title: localization.ERROR,
        text: localization.COPY_ASSETS_DIALOG.saveCopyPresetError,
      });
    } finally {
      setIsLoading(false);
    }
  }, [downloadPresets, selectedPreset, selectedSettings, updateDownloadPresets]);

  const handleSavePreset = useCallback(() => {
    const { _id, name } = selectedPresetView;
    const { teamId: isShared } = selectedPresetView;
    const isNew = _id === customPreset._id;
    const presetName = isNew ? '' : name;
    const title = isNew
      ? localization.COPY_ASSETS_DIALOG.createCopyPreset
      : localization.COPY_ASSETS_DIALOG.saveCopyPreset;

    showDialog({
      title,
      input: {
        label: 'Name',
        type: 'text',
        value: presetName,
      },
      checkbox: {
        value: !!isShared,
        label: 'Share with my team',
      },
      disableOk: ({ input }) => !input,
      onOk: ({ input: nextName, checkbox: shared }) => {
        saveDownloadPreset(nextName, shared, isNew);
      },
    });
  }, [saveDownloadPreset, selectedPresetView]);

  const removeDownloadPreset = useCallback(async (presetId) => {
    try {
      setIsLoading(true);
      await downloadPresetActions.deleteDownloadPreset(presetId);

      removePreset(presetId);
    } catch (e) {
      showDialog({
        title: localization.ERROR,
        text: localization.COPY_ASSETS_DIALOG.removeCopyPresetError,
      });
    } finally {
      setIsLoading(false);
    }
  }, [removePreset]);

  const handleRemovePreset = useCallback((presetId, presetName) => {
    showDialog({
      title: localization.COPY_ASSETS_DIALOG.removeCopyPreset,
      text: localization.COPY_ASSETS_DIALOG.removeCopyPresetText(presetName),
      onOk: () => {
        removeDownloadPreset(presetId);
      },
    });
  }, [removeDownloadPreset]);

  const handleClose = useCallback(() => {
    if (typeof onCancel === 'function') onCancel();
    destroy();
  }, [destroy, onCancel]);

  const handleSubmit = useCallback(() => {
    if (typeof onConfirm === 'function') {
      onConfirm({
        presetId: selectedPresetId,
        ...selectedSettings,
      });
    }
    destroy();
  }, [destroy, onConfirm, selectedPresetId, selectedSettings]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.keyCode) {
      case 27:
        handleClose();
        break;
      case 13:
        handleSubmit();
        break;
      default:
        break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, handleSubmit]);

  return (
    <div className="simpleDialog downloadDialog assetsCopyDialog">
      <div className="simpleDialogUnderlayer" />
      <div className="simpleDialogBox">
        <div className="simpleDialogHeader">
          <h2 className="simpleDialogTitle">{localization.COPY_ASSETS_DIALOG.title}</h2>
          <button
            className="simpleDialogBtnCross"
            onClick={handleClose}
            type="button"
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <If condition={isMainApp}>
              <Presets
                isLoading={isLoading}
                selectedPreset={selectedPresetView}
                presets={presets}
                onChange={handleChangePreset}
                onRemove={handleRemovePreset}
                onSave={handleSavePreset}
              />
            </If>
            <If condition={!shouldFormatBeDisabled}>
              <Settings
                selectedSettings={selectedSettings}
                onChange={handleChangeSettings}
                isDisabledFormat={shouldFormatBeDisabled}
                disablePdfFormat={false}
                onBlur={handleBlurSettings}
                sourceMimeType={sourceMimeType}
              />
            </If>
          </div>
        </div>
        <div className="simpleDialogFooter">
          <Button
            color="secondary"
            size="md"
            variant="contained"
            onClick={handleClose}
          >
            {localization.COPY_ASSETS_DIALOG.btnCancel}
          </Button>
          <Button
            color="primary"
            size="md"
            variant="contained"
            onClick={handleSubmit}
            disabled={selectedSettings.mimeType === 'custom'}
          >
            {localization.COPY_ASSETS_DIALOG.btnOk}
          </Button>
        </div>
      </div>
    </div>
  );
}

AssetsCopyDialog.defaultProps = {
  shouldFormatBeDisabled: false,
  onConfirm: null,
  onCancel: null,
  downloadPresets: [],
  sourceMimeType: undefined,
};

AssetsCopyDialog.propTypes = {
  destroy: PropTypes.func.isRequired,
  shouldFormatBeDisabled: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  downloadPresets: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
  })),
  setDownloadPresets: PropTypes.func.isRequired,
  sourceMimeType: PropTypes.string,
};

export default connect(
  (state) => ({
    downloadPresets: Array.isArray(state.user.presets) ? state.user.presets : [],
  }),
  { setDownloadPresets },
)(AssetsCopyDialog);
